<script setup lang="ts">
import {
  SelectionType,
  SelfAssessmentStep,
} from "~/classes/models/self-assessment/self-assessment-step.model";
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";
import { useGetRangeFromAttrValue } from "~/composables/quill-selection/useGetRangeFromAttrValue";

type DuplicateAttributeReviewItem = {
  step: SelfAssessmentStep;
  attribute: string;
  instanceId: string;
  label: string;
};

const props = defineProps({
  quillDelta: {
    type: Object as PropType<any>,
    required: true,
  },
  selectedDataAttribute: {
    type: String,
    required: true,
  },
  selectionType: {
    type: String as PropType<SelectionType>,
    required: true,
  },
  quillEditorSelector: {
    type: String,
    required: true,
  },
  documentSelector: {
    type: String,
    required: true,
  },
  scrollOffsetY: {
    type: Number,
    required: true,
  },
  wordProcessorInstanceId: {
    type: String,
    required: true,
  },
  duplicateAttributePrefix: {
    type: String,
  },
  duplicateCompatibleSteps: {
    type: Array as PropType<SelfAssessmentStep[]>,
    default: () => [],
  },
});

const emit = defineEmits(["removeInstance"]);

const wordProcessorStore = useWordProcessorStore(props.wordProcessorInstanceId);

const getDuplicateAttributeItems = (elements: Element[]) => {
  if (props.duplicateAttributePrefix == undefined) return [];
  if (props.duplicateCompatibleSteps.length == 0) return [];

  const stepByAttribute = new Map(
    props.duplicateCompatibleSteps.map((step) => [
      step.criteriaAttribute.attribute,
      step,
    ])
  );

  const itemsByKey = new Map<string, DuplicateAttributeReviewItem>();

  for (const element of elements) {
    const duplicateAttributeNames = element.getAttributeNames().filter((name) =>
      name.startsWith(props.duplicateAttributePrefix!)
    );

    for (const attribute of duplicateAttributeNames) {
      const step = stepByAttribute.get(attribute);
      const instanceId = element.getAttribute(attribute);

      if (step == undefined || instanceId == undefined) continue;

      itemsByKey.set(`${attribute}:${instanceId}`, {
        step,
        attribute,
        instanceId,
        label: step.label,
      });
    }
  }

  return Array.from(itemsByKey.values());
};

watch(
  () => props.selectedDataAttribute,
  () => {},
  { immediate: true }
);

const instances = computed(() => {
  const quill = wordProcessorStore.getQuillInstance();
  if (!quill) return [];

  if (props.selectedDataAttribute == undefined) return [];

  if (props.selectedDataAttribute == "") return [];

  const elements = document.querySelectorAll(
    `[${props.selectedDataAttribute}]`
  );

  // Groups the elements by the data[selectedDataAttribute] value
  const groups = {} as any;
  elements.forEach((element) => {
    const value = element.getAttribute(props.selectedDataAttribute);

    if (value == null) return;

    if (!groups[value]) {
      groups[value] = [];
    }
    groups[value].push(element);
  });

  const instances = Object.keys(groups);

  const instancesData = instances.map((instance) => {
    const ranges = useGetRangeFromAttrValue(
      props.quillDelta,
      props.selectedDataAttribute,
      instance
    );

    if (ranges.length == 0) return undefined;

    let start = ranges[0].index;
    let length = 0;

    for (const range of ranges) {
      length += range.length;
    }

    switch (props.selectionType) {
      case SelectionType.sentence:
        length = length + 1;
        break;
    }

    return {
      range: {
        start: start,
        end: length,
        text: quill.getText(start, length),
      },
      instanceId: instance,
      duplicateAttributeItems: getDuplicateAttributeItems(groups[instance]),
    };
  });

  return instancesData.filter((instance) => instance != undefined);
});
</script>

<template>
  <div>
    <DocumentSelectionHighlightButton
      v-for="instance in instances"
      :key="instance.instanceId"
      :instance="instance"
      :quillRange="instance.range"
      :selectionType="props.selectionType"
      :quillEditorSelector="quillEditorSelector"
      :scrollOffsetY="scrollOffsetY"
      type="selected"
      :documentSelector="documentSelector"
      :duplicate-attribute-items="instance.duplicateAttributeItems"
      @click="emit('removeInstance', instance.instanceId)"
      @remove-duplicate-attribute="
        (item) =>
          emit('removeInstance', item.instanceId, {
            stepOverride: item.step,
            attributeOverride: item.attribute,
          })
      "
    />
  </div>
  <!-- <DocumentSelectionSelectedButton
    v-for="(instance, key) in instances"
    :key="instance.instanceId"
    :instance="instance"
    @removeInstance="emit('removeInstance', instance.instanceId)"
  /> -->
</template>

<style scoped></style>
